import { useSearchParams } from "react-router-dom";

export type StepId =
  | "define-version-settings"
  | "define-case-properties"
  | "review-additional-inputs"
  | "configure-file-categories"
  | "assign-review-roles"
  | "select-decision-flow"
  | "configure-case-actions"
  | "define-tasks"
  | "define-workflow-rules";

export type Step = {
  id: StepId;
  title: string;
  description: string;
};

export const STEPS: Step[] = [
  {
    id: "define-version-settings",
    title: "Define version settings",
    description: "Provide basic details for the template",
  },
  {
    id: "define-case-properties",
    title: "Define case properties",
    description:
      "Define the properties of the cases created from this template",
  },
  {
    id: "review-additional-inputs",
    title: "Review additional inputs",
    description:
      "These additional inputs will be collected at case creation and used to populate case blocks, in addition to case properties.",
  },
  {
    id: "configure-file-categories",
    title: "Configure file categories",
    description:
      "Set up file categories to better organize the documents associated with each case. Categories help structure how files are displayed and grouped, making it easier to review, upload, and manage.",
  },
  {
    id: "assign-review-roles",
    title: "Assign review roles",
    description: "Assign the roles for the cases created from this template",
  },
  {
    id: "define-tasks",
    title: "Define tasks",
    description:
      "Specify the tasks reviewers should complete while investigating the case",
  },
  {
    id: "configure-case-actions",
    title: "Configure case actions",
    description: "Specify the actions reviewers can take on a case.",
  },
  {
    id: "define-workflow-rules",
    title: "Define workflow rules",
    description:
      "Specify what tasks and actions are available at each status of the case workflow.",
  },
  {
    id: "select-decision-flow",
    title: "Case preparation flow",
    description:
      "Optionally select a Decision Flow to automatically prepare a case for review, e.g., by adding an AI summary.",
  },
] as const;

export const getStepById = (stepId: StepId): Step => {
  const step = STEPS.find((step) => step.id === stepId);
  return step as Step;
};

export const useCurrentStep = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStepId = searchParams.get("step");
  const isValidStep = rawStepId && STEPS.some((step) => step.id === rawStepId);
  const currentStepId = isValidStep ? rawStepId : STEPS[0].id;

  const setCurrentStep = (stepId: StepId) => {
    const step = getStepById(stepId);
    if (step) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("step", stepId);
        return newParams;
      });
    }
  };

  return [currentStepId as StepId, setCurrentStep] as const;
};
